// After the meeting — the customer's last screen should feel like ClikHome, not like a closed tab.
import { clearToasts, firstName, h } from "./dom.js";
import { isCanvas, product, words, wordmark } from "./product.js";
import { legalText } from "./legal.js";

const COPY = {
  left: (you, m) => you.role === "host"
    ? ["You have left the meeting.", "The room stays open for the others until the last person leaves."]
    : [`Thank you, ${firstName(you.name)}.`, words.thanks(you, m)],
  ended: (you, m) => you.role === "host"
    ? ["The meeting has ended.", "Everyone has left the room."]
    : [`Thank you, ${firstName(you.name)}.`, words.thanksEnded(you, m)],
  removed: () => ["You have left the meeting.", `${words.removedBy} removed you from the meeting, so this link no longer opens it. If that was a mistake, ask them for a new link.`],
  elsewhere: () => ["You joined from another screen.", "The meeting moved to your other device. Rejoin here to bring it back."],
  lost: () => ["The connection was lost.", "Your internet connection dropped, and we could not get you back into the meeting in ten minutes of trying. Rejoin when you are back online."],
};

// The person filming at the site is not the customer: no thank-you from the sales manager, no link to the project.
const SITE = {
  left: () => ["The site camera is off.", "Thank you. Open the same link again to carry on while the meeting is on."],
  ended: (you, m) => ["The meeting has ended.", `Thank you for filming. ${firstName(m.hostName)} has ended the meeting.`],
};

export function showEnd(root, info, reason, rejoin) {
  clearToasts(); // the call's notices end with it (audit U9)
  const [head, line] = ((info.you.role === "site" && SITE[reason]) || COPY[reason] || COPY.lost)(info.you, info.meeting);
  // A Canvas meeting the sales manager ended, or a person he removed, cannot come back: no button that cannot work. A
  // ClikMeet room's link stands after its call is ended (CR-20260926-001 F4), so it offers to join again.
  // A guest the door let in lost their own link when the host ended the call (Ravi, 26 Sep): they ask at the door again.
  let door = null;
  try { door = sessionStorage.getItem(`clikmeet-door:${info.you.code}`); } catch { /* storage refused */ }
  const roomAgain = Boolean(info.inRoom) && reason === "ended";
  const viaDoor = roomAgain && info.you.role === "guest" && door;
  const canRejoin = rejoin && !viaDoor && (roomAgain || !["ended", "removed"].includes(reason));
  const actions = h("div", { class: "actions" },
    viaDoor ? h("a", { class: "btn", href: `/r/${encodeURIComponent(door)}`, text: "Ask to join again" }) : null,
    canRejoin ? h("button", { class: "btn", type: "button", text: roomAgain ? "Join again" : "Rejoin", onclick: rejoin }) : null,
    ["host", "site"].includes(info.you.role) || !words.explore ? null : h("a", { class: "btn btn-ghost", href: words.explore.href, text: words.explore.text }));
  root.replaceChildren(h("div", { class: "page" },
    h("header", { class: "page-head" }, words.owner()),
    h("main", { class: "page-main" }, h("div", { class: "end" },
      h("p", { class: "eyebrow", text: info.meeting.project || "Your meeting" }),
      h("h1", { class: "statement", text: head }),
      h("p", { class: "lede", text: line }),
      actions)),
    // Canvas signs off under the builder's name, with its own mark at the foot; ClikMeet's name is already at the top.
    // g-legal-line (CR-20261009-001): and who legally stands behind ClikHome, under the mark — text only
    isCanvas ? h("footer", { class: "page-foot" }, h("a", { class: "brand canvas-brand", href: "https://clikin.ai/" }, h("b", { text: "Walkin" }), h("span", { class: "by", text: " by Clikin.ai" })),
      h("p", { class: "legal", text: legalText(info) })) : null));
  document.title = product.title;
}

export function showProblem(root, title, line) {
  root.replaceChildren(h("div", { class: "page" },
    h("header", { class: "page-head" }, words.owner()),
    h("main", { class: "page-main" }, h("div", { class: "end" },
      h("h1", { class: "statement", text: title }),
      h("p", { class: "lede", text: line }),
      words.explore ? h("div", { class: "actions" }, h("a", { class: "btn btn-ghost", href: words.explore.href, text: words.explore.visit })) : null))));
  document.title = words.problemTitle(title);
}

// A lost connection, being rejoined by itself (main.js rejoin; Ravi, 28 Sep): the page waits for the network and goes
// back in with the same camera and microphone. The person can leave instead.
export function showRejoining(root, info, onLeave) {
  clearToasts();
  document.body.classList.remove("ms-dark");
  root.replaceChildren(h("div", { class: "page" },
    h("header", { class: "page-head" }, words.owner()),
    h("main", { class: "page-main" }, h("div", { class: "end rejoining", role: "status", "aria-live": "polite" },
      h("p", { class: "eyebrow", text: info.meeting.project || "Your meeting" }),
      h("h1", { class: "statement" }, h("span", { class: "pulse" }), "Connection lost — rejoining…"),
      h("p", { class: "lede", text: "Keep this page open. As soon as your connection is back you will be put back into the meeting, with your camera and microphone as you had them." }),
      h("div", { class: "actions" }, h("button", { class: "btn btn-ghost", type: "button", text: "Leave the meeting",
        onclick: (e) => { e.currentTarget.disabled = true; e.currentTarget.textContent = "Leaving…"; onLeave(); } }))))));
  document.title = product.title;
}
